import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { motion } from 'framer-motion'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import FlagIcon from '@mui/icons-material/Flag'
import RateReviewIcon from '@mui/icons-material/RateReview'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import BigModal from '../../../../components/Modals/BigModal'
import EmptyState from '../../../../components/EmptyState/EmptyState'
import StatTile from '../../../userPages/dashboard/managerOverview/StatTile'
import { viz } from '../../../userPages/dashboard/managerOverview/overviewTheme'
import { SubmissionReview } from '../employeeSingle/OccupationalHealthTab'
import OHHero from './OHHero'
import OHStaffTable, { FILTERS } from './OHStaffTable'
import {
  BmiDistributionChart,
  DeclarationsChart,
  FitnessCoverageCard,
  FlagBreakdownChart,
  ImmunisationCoverageChart,
  RoleCoverageTable,
  SubmissionsTrendChart,
} from './OHCharts'

const sectionMotion = (index) => ({
  component: motion.div,
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay: 0.08 * index, ease: 'easeOut' },
})

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// How the screening is set up, so a reviewer can see at a glance what the
// numbers are measured against
const SetupCard = ({ settings }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Stack mb={1.5}>
      <Typography variant="title">Screening setup</Typography>
      <Typography variant="smallGrey">
        What the questionnaire currently asks
      </Typography>
    </Stack>
    <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
      <Chip
        size="small"
        variant="outlined"
        label={plural(settings.immunisations, 'immunisation')}
      />
      <Chip
        size="small"
        variant="outlined"
        label={plural(settings.fitnessChecks, 'fitness check')}
      />
      <Chip
        size="small"
        variant="outlined"
        label={plural(settings.conditions, 'condition')}
      />
      <Chip
        size="small"
        variant="outlined"
        label={plural(settings.customQuestions, 'custom question')}
      />
    </Stack>
    <Stack gap={0.5}>
      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
        Voluntary recompletion:{' '}
        <strong>
          {settings.allowSelfRecomplete ? 'allowed' : 'only when asked'}
        </strong>
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
        Shift booking while outstanding:{' '}
        <strong>{settings.blockShiftBooking ? 'blocked' : 'allowed'}</strong>
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
        Recurrence: <strong>confirmed at each appraisal</strong>
      </Typography>
    </Stack>
    <Button
      component={Link}
      to="/site-configuration/occupational-health"
      size="small"
      sx={{ mt: 1.5, alignSelf: 'flex-start' }}
    >
      Edit configuration
    </Button>
  </Paper>
)

const OccupationalHealthDashboard = () => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { setToast } = useContext(GlobalContext)
  const [searchParams, setSearchParams] = useSearchParams()

  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [viewingID, setViewingID] = useState(null)
  const [requestingId, setRequestingId] = useState(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkRequesting, setBulkRequesting] = useState(false)

  const filterParam = searchParams.get('filter')
  const filter = FILTERS.some((item) => item.key === filterParam)
    ? filterParam
    : 'all'
  const setFilter = (next) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'all') params.delete('filter')
    else params.set('filter', next)
    setSearchParams(params, { replace: true })
  }

  const load = useCallback(() => {
    // API never rejects on 4xx/5xx - check the status explicitly
    API.get('/occupational-health/dashboard')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setError(null)
        } else if (res.status === 403) {
          setError(
            'You need the Personnel, Manager or Supervisor team to view this dashboard.',
          )
        } else {
          setError(
            "We couldn't load the occupational health dashboard. Please try again shortly.",
          )
        }
      })
      .catch((e) => {
        console.error(e)
        setError(
          "We couldn't load the occupational health dashboard. Please try again shortly.",
        )
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const requestOne = (person) => {
    setRequestingId(person._id)
    API.post(`/occupational-health/employee/${person._id}/request`)
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `Screening requested from ${person.userAccount?.firstName ?? 'employee'}`,
          })
          load()
        } else {
          setToast({
            message: 'Could not request the screening',
            severity: 'error',
          })
        }
      })
      .finally(() => setRequestingId(null))
  }

  const outstandingNotRequested = (data?.staff ?? []).filter(
    (person) => person.state === 'never',
  )

  const requestBulk = () => {
    setBulkRequesting(true)
    API.post('/occupational-health/request-bulk', {
      employeeIds: outstandingNotRequested.map((person) => person._id),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `Screening requested from ${plural(res.data.requested, 'staff member')}`,
          })
          setBulkOpen(false)
          load()
        } else {
          setToast({
            message: 'Could not send the requests',
            severity: 'error',
          })
        }
      })
      .finally(() => setBulkRequesting(false))
  }

  if (error) return <Alert severity="error">{error}</Alert>

  if (!data) {
    return (
      <Stack gap={2}>
        <Skeleton variant="rounded" height={240} />
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={6} sm={4} lg={2} key={i}>
              <Skeleton variant="rounded" height={96} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Skeleton variant="rounded" height={320} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Skeleton variant="rounded" height={320} />
          </Grid>
        </Grid>
      </Stack>
    )
  }

  if (!data.enabled) {
    return (
      <EmptyState
        icon={HealthAndSafetyIcon}
        title="Occupational health screening is switched off"
        description="Screening is disabled in your organisation's configuration, so there is nothing to report here. Turn it on to start collecting health declarations from your staff."
        actions={[
          {
            label: 'Open configuration',
            to: '/site-configuration/occupational-health',
          },
        ]}
        docsHref="/docs/employees-hr/occupational-health"
      />
    )
  }

  const { totals, settings } = data
  const screened = totals.complete
  const hasSubmissions = data.staff.some((person) => person.latestSubmission)

  return (
    <Stack gap={2.5} pb={4}>
      <OHHero
        orgSettings={orgSettings}
        totals={totals}
        requesting={bulkRequesting}
        onRequestOutstanding={() => setBulkOpen(true)}
      />

      {totals.activeStaff === 0 ? (
        <EmptyState
          icon={HealthAndSafetyIcon}
          title="No active staff yet"
          description="The dashboard reports on employees with a New Starter or Live status. Add your staff and their screening status will appear here."
          actions={[{ label: 'Add employees', to: '/employees/new' }]}
          docsHref="/docs/employees-hr/occupational-health"
        />
      ) : (
        <>
          <Box {...sectionMotion(1)}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} lg={2}>
                <StatTile
                  icon={FactCheckIcon}
                  label="Screened"
                  value={totals.complete}
                  hint={`of ${totals.activeStaff} active staff`}
                  accent={viz.good}
                  url="/occupational-health?filter=complete"
                />
              </Grid>
              <Grid item xs={6} sm={4} lg={2}>
                <StatTile
                  icon={PendingActionsIcon}
                  label="Never completed"
                  value={totals.neverCompleted}
                  hint="No screening on record"
                  accent={totals.neverCompleted > 0 ? viz.critical : viz.good}
                  url="/occupational-health?filter=outstanding"
                />
              </Grid>
              <Grid item xs={6} sm={4} lg={2}>
                <StatTile
                  icon={ForwardToInboxIcon}
                  label="Requested"
                  value={totals.requested}
                  hint="Waiting for staff"
                  accent={totals.requested > 0 ? viz.warning : viz.good}
                  url="/occupational-health?filter=outstanding"
                />
              </Grid>
              <Grid item xs={6} sm={4} lg={2}>
                <StatTile
                  icon={FlagIcon}
                  label="Flagged"
                  value={totals.flagged}
                  hint={
                    screened > 0
                      ? `${Math.round((totals.flagged / screened) * 100)}% of screened`
                      : 'Answers needing review'
                  }
                  accent={totals.flagged > 0 ? viz.warning : viz.good}
                  url="/occupational-health?filter=flagged"
                />
              </Grid>
              <Grid item xs={6} sm={4} lg={2}>
                <StatTile
                  icon={RateReviewIcon}
                  label="Appraisal refresh"
                  value={totals.awaitingAppraisalRefresh}
                  hint="Open appraisals awaiting confirmation"
                  accent={
                    totals.awaitingAppraisalRefresh > 0 ? '#268EBB' : viz.good
                  }
                  url="/occupational-health?filter=appraisal"
                />
              </Grid>
              <Grid item xs={6} sm={4} lg={2}>
                <StatTile
                  icon={AttachFileIcon}
                  label="With evidence"
                  value={totals.evidenceUploaded}
                  hint="Immunisation files uploaded"
                  accent="#268EBB"
                />
              </Grid>
            </Grid>
          </Box>

          {!hasSubmissions ? (
            <Box {...sectionMotion(2)}>
              <EmptyState
                icon={HealthAndSafetyIcon}
                title="No screenings completed yet"
                description="Charts appear once staff start submitting their occupational health questionnaire. Staff are prompted on their home screen, and you can request screenings from everyone outstanding above."
                steps={[
                  'Check the questionnaire under Configuration > Employees > Occupational Health',
                  'Use "Request from everyone outstanding" to notify staff',
                  'Staff complete the form on My Occupational Health or at their next appraisal',
                ]}
                actions={[
                  {
                    label: 'Request from everyone outstanding',
                    onClick: () => setBulkOpen(true),
                    disabled: outstandingNotRequested.length === 0,
                  },
                  {
                    label: 'Open configuration',
                    to: '/site-configuration/occupational-health',
                  },
                ]}
                docsHref="/docs/employees-hr/occupational-health"
              />
            </Box>
          ) : (
            <Box {...sectionMotion(2)}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Stack gap={2}>
                    <ImmunisationCoverageChart
                      immunisations={data.immunisations}
                    />
                    <FlagBreakdownChart
                      flagCategories={data.flagCategories}
                      screened={screened}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <BmiDistributionChart bmi={data.bmi} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SubmissionsTrendChart
                          submissionsByMonth={data.submissionsByMonth}
                        />
                      </Grid>
                    </Grid>
                    <DeclarationsChart
                      conditions={data.conditions}
                      covidRisks={data.covidRisks}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Stack gap={2}>
                    <FitnessCoverageCard fitness={data.fitness} />
                    <RoleCoverageTable byRole={data.byRole} />
                    <SetupCard settings={settings} />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box {...sectionMotion(3)}>
            <OHStaffTable
              staff={data.staff}
              filter={filter}
              onFilterChange={setFilter}
              onView={setViewingID}
              onRequest={requestOne}
              requestingId={requestingId}
            />
          </Box>
        </>
      )}

      <BigModal open={!!viewingID} onClose={() => setViewingID(null)}>
        <Stack padding={2}>
          {viewingID && <SubmissionReview submissionID={viewingID} />}
        </Stack>
      </BigModal>

      <Dialog
        open={bulkOpen}
        onClose={() => !bulkRequesting && setBulkOpen(false)}
      >
        <DialogTitle>Request screenings from everyone outstanding?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {plural(outstandingNotRequested.length, 'active staff member')}{' '}
            {outstandingNotRequested.length === 1 ? 'has' : 'have'} never
            completed a screening and{' '}
            {outstandingNotRequested.length === 1 ? "hasn't" : "haven't"} been
            asked yet. Each will get a notification and a prompt on their home
            screen. Staff with a request already open are not contacted again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkRequesting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={requestBulk}
            disabled={bulkRequesting || outstandingNotRequested.length === 0}
          >
            {bulkRequesting ? 'Sending…' : 'Send requests'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default OccupationalHealthDashboard
